import { type RefObject, useCallback, useRef, useState } from "react";
import {
  clamp,
  MAX_SCALE,
  MIN_SCALE,
  type Point,
  type View,
  viewBoxToWorld,
  zoomAt,
} from "./geometry.ts";

/**
 * Pan + zoom for the floor-plan SVG. The transform lives on an inner <g>, so
 * pointer→viewBox mapping uses the svg's screen CTM directly (independent of the
 * transform), then we divide out the view to get world coordinates.
 */
export function usePanZoom(svgRef: RefObject<SVGSVGElement | null>) {
  const [view, setView] = useState<View>({ tx: 0, ty: 0, scale: 1 });
  const pan = useRef<{ startVbX: number; startVbY: number; tx: number; ty: number } | null>(null);

  const toViewBox = useCallback(
    (clientX: number, clientY: number): Point => {
      const svg = svgRef.current;
      // jsdom / SSR have no SVG CTM — fall back to origin so handlers stay safe.
      if (
        !svg ||
        typeof svg.getScreenCTM !== "function" ||
        typeof svg.createSVGPoint !== "function"
      ) {
        return { x: 0, y: 0 };
      }
      const ctm = svg.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const pt = svg.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const mapped = pt.matrixTransform(ctm.inverse());
      return { x: mapped.x, y: mapped.y };
    },
    [svgRef],
  );

  const screenToWorld = useCallback(
    (clientX: number, clientY: number): Point => {
      const vb = toViewBox(clientX, clientY);
      return viewBoxToWorld(view, vb.x, vb.y);
    },
    [toViewBox, view],
  );

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const vb = toViewBox(e.clientX, e.clientY);
      const factor = Math.exp(-e.deltaY * 0.0015);
      setView((v) => zoomAt(v, vb.x, vb.y, factor));
    },
    [toViewBox],
  );

  const startPan = useCallback(
    (e: React.PointerEvent) => {
      const vb = toViewBox(e.clientX, e.clientY);
      pan.current = { startVbX: vb.x, startVbY: vb.y, tx: view.tx, ty: view.ty };
      const move = (ev: PointerEvent) => {
        const p = pan.current;
        if (!p) return;
        const cur = toViewBox(ev.clientX, ev.clientY);
        setView((v) => ({
          ...v,
          tx: p.tx + (cur.x - p.startVbX),
          ty: p.ty + (cur.y - p.startVbY),
        }));
      };
      const up = () => {
        pan.current = null;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [toViewBox, view.tx, view.ty],
  );

  const zoomBy = useCallback((factor: number) => {
    setView((v) => {
      // zoom around the current view centre is approximated by zooming at (0,0)
      // of viewBox; good enough for the +/- buttons.
      const next = clamp(v.scale * factor, MIN_SCALE, MAX_SCALE);
      return { ...v, scale: next };
    });
  }, []);

  const reset = useCallback(() => setView({ tx: 0, ty: 0, scale: 1 }), []);

  return { view, screenToWorld, onWheel, startPan, zoomBy, reset };
}
